import * as React from 'react';
import * as sessionStorage from '../session-storage';
import { type PageBase, type PageError, type PageOrError } from '../current-page/state';
import { currentUrl, URLX } from '../url';
import { type AsyncObservable, createEvent } from '../events';
import { scrollPosition } from '../scrolling';
import { latestPageRenderWasOnTheServer } from '../latest-render-was-on-the-server';

export type ClientParams = Record<string, string | number | boolean>;

type ComponentState = { id: string; state: any };

type HistoryState = {
  url: string;
  // An unique id to identify a state object
  stateId: string;
  // The estimated index for this state objects position in the browser history.
  // That is, the object with index 0 is the oldest and first state in the history.
  // And the object with index `history.length - 1` is the newest. This number is
  // incremented from the current state, so the process could look like:
  // 1. User visits / - state object with index 0
  // 2. User visits /smink - state object with index 1
  // 3. User visits /smink/hudtyp - state object with index 2
  // 4. User hits back twice to get to the start page
  // 5. User visits /harvard - state object with index 1 and the states for /smink and /smink/hudtyp are gone
  stateIndex: number;
  clientParams?: ClientParams;
  componentStates?: ComponentState[];
};

export const enum HistoryEventType {
  Push = 'push',
  Pop = 'pop',
  Replace = 'replace',
}

// These types are used when the user initiates a state change but we are unable
// to load the page. The process looks like this:
// 1. User visits /
// 2. User clicks on a link to /smink
// 3. The address bar now shows /smink, even if we're still on the start page with a loading spinner
// 4. The page /smink fails to load
// 5. The state change is reverted so the address bar shows / again
// 6. The user is presented with an error with a Retry-button
// 7. If she clicks on the Retry button, we go back to point 3.
type RevertStateChange = () => RetryStateChange;
type RetryStateChange = () => boolean;

export type NavigationInterceptor = (
  navigationType: 'push' | 'replace',
  url: string | URLX,
  options?: HistoryOptions,
) => void;
let navigationInterceptor: NavigationInterceptor | undefined = undefined;

export function setNavigationInterceptor(interceptor: NavigationInterceptor) {
  navigationInterceptor = interceptor;
}

export type HistoryOptions = {
  clientParams?: ClientParams;
  linkIdentifier?: string;
  deferLoadPromise?: Promise<any>;
  page?: PageBase | AsyncObservable<PageBase | PageError>;
  scrollPosition?: number;
  skipLoad?: boolean;
  componentName?: string;
  shouldFetchData?: boolean | ((cachedData: Excite.ContentApi.IComponentData) => boolean);
  shouldUseCache?: boolean | ((cachedData: Excite.ContentApi.IComponentData) => boolean);
  uiState?: unknown;
  skipUpdateScrollPosition?: boolean;
  keepComponents?: boolean;
  merge?: (page: PageOrError, newContent: any) => PageOrError;
  extendPartialCache?: (partialCache: PageOrError) => PageBase | undefined;
  initiator?: 'navigationInterceptor';
};

export type HistoryEvent = {
  url: URLX;
  event: HistoryEventType;
  state: HistoryState;
  previousState: HistoryState;
  options: HistoryOptions;
  revert: RevertStateChange;
  scrollPosition: number | undefined;
};

export const [historyEvent] = createEvent<HistoryEvent>();

export function useOnNavigation(callback: (e: HistoryEvent) => void) {
  const callbackRef = React.useRef(callback);
  callbackRef.current = callback;

  React.useEffect(() => {
    let previousUrl = currentUrl().href;
    const unsubscribe = historyEvent.on(e => {
      const url = e.url.href;
      if (previousUrl !== url) {
        callbackRef.current(e);
      }
      previousUrl = url;
    });
    return unsubscribe;
  }, []);
}

function parseClientParam<T extends string | number | boolean = string>(
  value: string | number | boolean | null,
  defaultValue: T,
): T {
  if (typeof defaultValue === 'number') {
    if (typeof value === 'number') {
      return value as T;
    }
    const parsed = Number(value);
    // We don't want to return `0` for '' and null, gotta love the fact that JavaScript gives you
    // 0 for Number(null) but NaN for Number(undefined)
    if (value === '' || value === null || isNaN(parsed)) {
      return defaultValue;
    }
    return parsed as T;
  }
  if (typeof defaultValue === 'boolean') {
    if (typeof value === 'boolean') {
      return value as T;
    }
    if (value === null || value === undefined) {
      return defaultValue;
    }
    const s = value.toString();
    if (s === '0' || s.toLowerCase() === 'false') {
      return false as T;
    }
    return true as T;
  }
  return (value ?? defaultValue) as T;
}

export function useClientParam(name: string, defaultValue: number): number;
export function useClientParam(name: string, defaultValue: boolean): boolean;
export function useClientParam(name: string, defaultValue: string): string;
export function useClientParam<T extends string | number | boolean = string>(
  name: string,
  defaultValue: T,
): T | undefined {
  const params = new URLSearchParams(currentUrl().search);
  const locationValue = params.get(name);
  if (!__BROWSER__) {
    return parseClientParam(locationValue, defaultValue);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [value, setValue] = React.useState<T | undefined>(parseClientParam<T>(locationValue, defaultValue));
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    return historyEvent.on(e => {
      // This ensures that we don't get re-renders when you click a link and the new page is loading
      // but the page with client params is still visible. Without this check you'd get a re-render
      // with the default value during the time the new page is loading.
      if (e.previousState && e.url.pathname !== new URL(e.previousState.url).pathname) {
        return;
      }

      if (e.state.clientParams && name in e.state.clientParams && e.state.clientParams[name] !== value) {
        setValue(parseClientParam(e.state.clientParams[name]?.toString(), defaultValue));
      } else if (value !== defaultValue) {
        setValue(defaultValue);
      }
    });
  }, [name, defaultValue, value]);
  return value;
}

// There's a difference between `currentState` and `window.history.state` in that
// when a `popstate` event is fired, `window.history.state` is already the state poped
// to and we need to know what the state was before that event.
let currentState: HistoryState = (() => {
  if (__BROWSER__) {
    if (window.history.state && window.history.state.stateId) {
      return window.history.state;
    } else {
      const url = new URLX(window.location.href).href;
      const state: HistoryState = {
        url,
        stateId: stateId(),
        stateIndex: 0,
      };
      window.history.replaceState(state, '', url);
      return state;
    }
  }
  return {
    url: '',
    stateId: '',
    stateIndex: 0,
  };
})();

let historyStates: HistoryState[] = (() => {
  if (!__BROWSER__) {
    return [];
  }
  const storedState = sessionStorage.getSessionStorage<HistoryState[]>('historystates', []);
  if (!storedState.find(s => s.stateId === currentState.stateId)) {
    return [currentState];
  }
  return storedState;
})();

export function setComponentState(state: ComponentState) {
  if (!currentState.componentStates) {
    currentState.componentStates = [];
  }

  const currentStateIndex = currentState.componentStates.findIndex(s => s.id === state.id);
  if (currentStateIndex !== -1) {
    currentState.componentStates[currentStateIndex] = state;
  } else {
    currentState.componentStates.push(state);
  }
}

export function getComponentState<TState>(id: string | undefined, defaultState: TState): TState {
  if (__BROWSER__ && id && !latestPageRenderWasOnTheServer()) {
    const states = historyStates.find(s => s.stateId === currentState.stateId)?.componentStates;
    return states?.find(state => state.id === id)?.state ?? defaultState;
  }
  return defaultState;
}

let ignoreNextPopStateEvent = false;

export function pushClientState(clientParams: ClientParams) {
  return pushState(currentUrl(), { clientParams });
}

export function pushState(url: URLX | string, options: HistoryOptions = {}) {
  if (navigationInterceptor && options.initiator !== 'navigationInterceptor') {
    navigationInterceptor('push', url, options);
    const [dummyEvent] = createEvent();
    return dummyEvent.emit(Promise.resolve());
  }

  if (typeof url === 'string') {
    url = new URLX(url);
  } else if (!(url instanceof URLX)) {
    throw new Error('Unknown type for `url` passed to pushState: ' + typeof url + ' ' + JSON.stringify(url));
  }
  url = getUrlWithClientParams(url, options);

  const href = url.href;

  const previousState = currentState;
  const index = historyStates.findIndex(s => s.stateId === currentState.stateId);
  if (index !== -1) {
    historyStates[index] = currentState;
  }
  saveScrollPosition(previousState, scrollPosition());

  const state: HistoryState = {
    stateId: stateId(),
    stateIndex: previousState.stateIndex + 1,
    // We clear `hiddenParam` here because when we get a `popstate` event
    // we don't want the `hiddenParam`. The hidden query string is used
    // to pass information to the server to only load a partial page
    // and if we go back/forward to that state, we want the full page.
    url: href,
    clientParams: options.clientParams,
  };

  let currentHistoryStatesIndex = historyStates.findIndex(s => s.stateId === currentState.stateId);
  if (currentHistoryStatesIndex === -1) {
    historyStates = [currentState];
    currentHistoryStatesIndex = 0;
  }
  historyStates = historyStates.slice(0, currentHistoryStatesIndex + 1);
  window.history.pushState(state, '', href);
  currentState = state;
  historyStates.push(currentState);
  return historyEvent.emit({
    event: HistoryEventType.Push,
    url,
    state,
    previousState,
    scrollPosition: 0,
    options,
    revert() {
      // Note that we pass `skipPopEvent: true` here.
      // This because we only want to revert the address bar change and not application
      // state. The application state has already been reverted here.
      historyBack(true);

      return () => {
        console.debug('Retrying reverted push state change to', href);
        pushState(url, options);
        return true;
      };
    },
  });
}

export function replaceClientState(clientParams: ClientParams) {
  return replaceState(currentUrl(), { clientParams });
}

export function replaceState(url?: URLX | string, options: HistoryOptions = {}) {
  if (!url) {
    url = new URLX(window.location.href);
  } else if (typeof url === 'string') {
    url = new URLX(url);
  }
  url = getUrlWithClientParams(url, options);

  if (navigationInterceptor && options.initiator !== 'navigationInterceptor') {
    navigationInterceptor('replace', url, options);
    const [dummyEvent] = createEvent();
    return dummyEvent.emit(Promise.resolve());
  }

  const href = url.href;

  const previousState = currentState;
  const state: HistoryState = {
    stateId: previousState.stateId,
    stateIndex: previousState.stateIndex,
    // We clear `hiddenParam` for the same reason as we do it in `pushState`
    url: href,
    clientParams: options.clientParams,
  };

  window.history.replaceState(state, '', href);
  currentState = state;

  return historyEvent.emit({
    event: HistoryEventType.Replace,
    url,
    state,
    scrollPosition: options.scrollPosition ?? scrollPosition(),
    previousState,
    options,
    revert() {
      window.history.replaceState(previousState, '', previousState.url);

      return () => {
        console.debug('Retrying reverted replace state change to', href);
        replaceState(url, options);
        return true;
      };
    },
  });
}

function getUrlWithClientParams(url: URLX, options: HistoryOptions) {
  if (options.clientParams) {
    url = new URLX(url.toString());
    for (const key in options.clientParams) {
      url.searchParams.set(key, options.clientParams[key]?.toString() ?? '');
    }
  }
  return url;
}

export function updateCurrentUrlWithoutEvents(url: URLX) {
  const href = url.href;
  currentState.url = href;
  window.history.replaceState(currentState, '', href);
}

export function historyBack(skipPopEvent = false) {
  ignoreNextPopStateEvent = skipPopEvent;
  window.history.back();
}

export function historyForward(skipPopEvent = false) {
  ignoreNextPopStateEvent = skipPopEvent;
  window.history.forward();
}

export function historyGo(index: number, skipPopEvent = false) {
  ignoreNextPopStateEvent = skipPopEvent;
  window.history.go(index);
}

if (__BROWSER__) {
  if (history.scrollRestoration) {
    history.scrollRestoration = 'manual';
  }

  window.addEventListener('popstate', e => {
    // When we revert a state change, we need to call `back()` which triggers
    // a `popstate` which we want to ignore
    if (ignoreNextPopStateEvent) {
      ignoreNextPopStateEvent = false;
      return;
    }

    // Safari triggers a popstate on load which we want to ignore
    if (!e.state || !(e.state as HistoryState).stateId) {
      return;
    }
    const previousState = currentState;
    saveScrollPosition(previousState, scrollPosition());
    const eventState: HistoryState = e.state;
    const eventStateIndex = eventState.stateIndex;
    const event: HistoryEventType =
      eventStateIndex < currentState.stateIndex ? HistoryEventType.Pop : HistoryEventType.Push;
    currentState = historyStates.find(s => s.stateId === eventState.stateId) ?? eventState;
    historyEvent.emit({
      url: new URLX(eventState.url),
      event,
      state: eventState,
      scrollPosition: getSavedScrollPosition(eventState),
      previousState,
      options: {},
      revert() {
        const revertToIndex = previousState.stateIndex - eventStateIndex;
        historyGo(revertToIndex, true);

        return () => {
          historyGo(eventStateIndex - previousState.stateIndex);
          return true;
        };
      },
    });
  });

  window.addEventListener('beforeunload', e => {
    saveScrollPosition(currentState, scrollPosition());
    sessionStorage.setSessionStorage('historystates', historyStates);
  });
}

function stateId() {
  return Math.ceil(Math.random() * 100000000).toString();
}

const scrollMemory: { [stateId: string]: number } = __BROWSER__
  ? sessionStorage.getSessionStorage('scrollMemory') || {}
  : {};
export function saveScrollPosition(state: HistoryState, position: number) {
  if (!state || !state.stateId || !scrollMemory) {
    return;
  }
  scrollMemory[state.stateId] = position;
  sessionStorage.setSessionStorage('scrollMemory', scrollMemory);
}

function getSavedScrollPosition(state: HistoryState) {
  if (!state || !state.stateId || !scrollMemory) {
    return undefined;
  }
  if (state.stateId in scrollMemory) {
    return scrollMemory[state.stateId];
  }
  return undefined;
}
